import {
  ArrowBack,
  CheckCircle,
  Google,
  InfoOutlined as InfoIcon,
  PersonAdd as PersonAddIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleSignInCompleteModal from '../components/GoogleSignInCompleteModal';
import '../components/Login.css';
import TermsAcceptanceCheckbox from '../components/TermsAcceptanceCheckbox';
import { TERMS_VERSION } from '../content/termsOfService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { clientApi } from '../services/clientApi';
import { colors } from '../theme';

const ClientRegister: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { loadClient } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGoogleCompleteModal, setShowGoogleCompleteModal] = useState(false);
  const [googleUserData, setGoogleUserData] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    user: any;
  } | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [weight, setWeight] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [age, setAge] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  
  const googleProvider = new GoogleAuthProvider();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validasyon
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      setError('Lütfen tüm zorunlu alanları doldurun.');
      showError('Lütfen tüm zorunlu alanları doldurun.', 'Eksik Bilgi');
      setLoading(false);
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      showError('Devam etmek için kullanım şartlarını kabul etmelisiniz.', 'Kullanım Şartları');
      setLoading(false);
      return;
    }
    setTermsError(false);

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      showError('Şifreler eşleşmiyor.', 'Hata');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      showError('Şifre en az 6 karakter olmalıdır.', 'Hata');
      setLoading(false);
      return;
    }

    try {
      // Önceki kullanıcının verilerini temizle
      const previousEmail = localStorage.getItem('userEmail');
      if (previousEmail && previousEmail !== email) {
        console.log('🔄 Farklı kullanıcı kaydı yapılıyor, eski veriler temizleniyor...');
        // Tüm kullanıcıya özel localStorage verilerini temizle
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('dietitianData');
        localStorage.removeItem('clientData');
        localStorage.removeItem('client-onboarding-completed');
        
        // TaroBot mesajlarını temizle
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('taro-messages-')) {
            localStorage.removeItem(key);
          }
          // Bildirimleri temizle
          if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
            localStorage.removeItem(key);
          }
        });
      }

      // ADIM 1: Firebase'de kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log('✅ Firebase kullanıcısı oluşturuldu:', email);

      // ADIM 2: Backend'de client profili oluştur (kilo, boy, yaş, cinsiyet dahil)
      try {
        const ageNum = age.trim() ? parseInt(age.trim(), 10) : undefined;
        const weightNum = weight.trim() ? parseFloat(weight.trim()) : undefined;
        const heightNum = height.trim() ? parseFloat(height.trim()) : undefined;
        await clientApi.createClient({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          age: ageNum != null && !isNaN(ageNum) ? ageNum : undefined,
          weight: weightNum != null && !isNaN(weightNum) ? weightNum : undefined,
          height: heightNum != null && !isNaN(heightNum) ? heightNum : undefined,
          gender: gender.trim() || undefined,
          isSelfRegistered: true,
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: TERMS_VERSION,
        });
        console.log('✅ Backend client profili oluşturuldu');

        showSuccess('Hesabınız başarıyla oluşturuldu!', 'Kayıt Başarılı');

        localStorage.setItem('userEmail', email);
        localStorage.setItem('userRole', 'client');
        // Yeni kullanıcı için tour'u göster
        localStorage.removeItem('client-onboarding-completed');

        // Dashboard'a yönlendir (dashboard'da diyetisyen seçme uyarısı gösterilecek)
        setTimeout(() => {
          navigate('/client');
        }, 1000);
      } catch (backendError: any) {
        // Backend'de hata oldu - Firebase kullanıcısını sil
        console.error('❌ Backend profil oluşturma hatası:', backendError);

        try {
          await userCredential.user.delete();
          console.log('🗑️ Firebase kullanıcısı silindi');
        } catch (deleteError) {
          console.error('Firebase kullanıcı silme hatası:', deleteError);
        }

        throw new Error(
          'Profil oluşturulurken bir hata oluştu. Lütfen tekrar deneyin. ' +
            `(Detay: ${backendError.message || 'Backend hatası'})`
        );
      }
    } catch (error: any) {
      console.error('❌ Hata:', error);

      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage =
          'Bu e-posta adresi zaten kullanılıyor. Giriş yapmayı deneyin.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifre en az 6 karakter olmalıdır.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Geçersiz e-posta adresi.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage, 'Kayıt Hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      // Önceki kullanıcının verilerini temizle
      const previousEmail = localStorage.getItem('userEmail');
      if (previousEmail) {
        console.log('🔄 Farklı kullanıcı kaydı yapılıyor, eski veriler temizleniyor...');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('dietitianData');
        localStorage.removeItem('clientData');
        localStorage.removeItem('client-onboarding-completed');
        
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('taro-messages-')) {
            localStorage.removeItem(key);
          }
          if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
            localStorage.removeItem(key);
          }
        });
      }

      // Google ile giriş yap
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const email = user.email;
      const displayName = user.displayName || '';
      
      if (!email) {
        throw new Error('Google hesabınızdan e-posta adresi alınamadı.');
      }

      console.log('✅ Google kaydı başarılı:', email);

      // Ad ve soyadı ayır (Google'dan gelen displayName'den)
      let firstName = '';
      let lastName = '';
      if (displayName) {
        const nameParts = displayName.trim().split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Modal'ı göster - kullanıcı ek bilgileri dolduracak
      setGoogleUserData({
        email,
        firstName: firstName || '',
        lastName: lastName || '',
        user,
      });
      setShowGoogleCompleteModal(true);
      setGoogleLoading(false);
    } catch (error: any) {
      console.error('❌ Google kayıt hatası:', error);

      let errorMessage = 'Google ile kayıt yapılırken bir hata oluştu.';

      // Firebase Authentication hataları
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Kayıt penceresi kapatıldı.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Kayıt penceresi engellendi. Lütfen popup engelleyicisini kapatın.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'Bu e-posta adresi ile farklı bir giriş yöntemi kullanılmış. Lütfen e-posta/şifre ile giriş yapın.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Bu e-posta adresi zaten kullanılıyor. Giriş yapmayı deneyin.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'İnternet bağlantınızı kontrol edin.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage, 'Google Kayıt Hatası');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCompleteModalSubmit = async (data: {
    firstName: string;
    lastName: string;
    phone?: string;
    age?: number;
    weight?: number;
    height?: number;
    gender?: string;
    acceptsOnlineClients?: boolean;
  }) => {
    if (!googleUserData) return;

    setGoogleLoading(true);
    try {
      console.log('📤 Backend\'e client oluşturma isteği gönderiliyor...');
      const result = await clientApi.createClient({
        firstName: data.firstName,
        lastName: data.lastName,
        email: googleUserData.email,
        phone: data.phone,
        age: data.age,
        weight: data.weight,
        height: data.height,
        gender: data.gender,
        isSelfRegistered: true,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION,
      });
      console.log('✅ Backend client profili oluşturuldu:', result);

      showSuccess('Google ile kayıt başarılı! Yönlendiriliyorsunuz...', 'Kayıt Başarılı');

      localStorage.setItem('userEmail', googleUserData.email);
      localStorage.setItem('userRole', 'client');
      // Yeni kullanıcı için tour'u göster
      localStorage.removeItem('client-onboarding-completed');

      // Login olduktan sonra client verisini Context'e yükle
      try {
        await loadClient(googleUserData.email);
      } catch (error) {
        console.error('Veri yükleme hatası (kritik değil):', error);
      }

      setShowGoogleCompleteModal(false);
      setGoogleUserData(null);

      // Dashboard'a yönlendir
      setTimeout(() => {
        navigate('/client');
      }, 1000);
    } catch (error: any) {
      console.error('❌ Profil oluşturma hatası:', error);
      throw error;
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCompleteModalCancel = async () => {
    if (googleUserData?.user) {
      try {
        await googleUserData.user.delete();
        console.log('🗑️ Firebase kullanıcısı silindi');
      } catch (deleteError) {
        console.error('Firebase kullanıcı silme hatası:', deleteError);
      }
    }
    await auth.signOut();
    setShowGoogleCompleteModal(false);
    setGoogleUserData(null);
    setGoogleLoading(false);
  };

  return (
    <div className="login-container">
      {/* Geri Butonu */}
      <IconButton
        onClick={() => navigate('/')}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          width: '48px',
          height: '48px',
          zIndex: 1000,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(30px)',
          color: 'rgba(0, 0, 0, 0.6)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: 'rgba(0, 0, 0, 0.8)',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ArrowBack />
      </IconButton>

      {/* Decorative Background Circles */}
      <div className="decorative-circles">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
      </div>

      {/* Main Content */}
      <div className="login-right">
        {/* Features Card */}
        <div className="features-card">
          <div className="brand-section-inline">
            <div className="brand-icon-inline">🥗</div>
            <div>
              <h1 className="brand-title-inline">Dietaro</h1>
              <p className="brand-subtitle-inline">
                Profesyonel Diyetisyen Platformu
              </p>
            </div>
          </div>

          <div className="features-list-inline">
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Diyetisyen Seçimi</h3>
                <p>Size en uygun diyetisyeni seçin</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Kişisel Program</h3>
                <p>Size özel diyet programları</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>İlerleme Takibi</h3>
                <p>Kilo ve yemek takibi</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Anlık İletişim</h3>
                <p>Diyetisyeninizle doğrudan iletişim</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="form-icon">
              <PersonAddIcon />
            </div>
            <h2 className="form-title">Danışan Kaydı</h2>
            <p className="form-subtitle">
              Hesabınızı oluşturun ve diyetisyen seçin
            </p>
          </div>

          <Box
            component="form"
            onSubmit={handleRegister}
            className="login-form"
          >
            {step === 1 && (
              <>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="Adınız"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ahmet"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label="Soyadınız"
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Yılmaz"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-posta Adresi"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@email.com"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#43A047',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#43A047',
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              fullWidth
              id="phone"
              label="Telefon (Opsiyonel)"
              name="phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0555 123 45 67"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#43A047',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#43A047',
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Şifre"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#43A047',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#43A047',
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Şifre Tekrar"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#43A047',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#43A047',
                    borderWidth: 2,
                  },
                },
              }}
            />

            <TermsAcceptanceCheckbox
              checked={termsAccepted}
              onChange={(v) => { setTermsAccepted(v); setTermsError(false); }}
              error={termsError}
              disabled={loading}
            />

            {error && step === 1 && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                }}
              >
                {error}
              </Alert>
            )}

            {/* Adım 1: Devam butonu */}
            <Button
              type="button"
              fullWidth
              variant="contained"
              disabled={loading || googleLoading}
              onClick={() => {
                setError('');
                if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
                  setError('Lütfen ad, soyad, e-posta ve şifre alanlarını doldurun.');
                  return;
                }
                if (password !== confirmPassword) {
                  setError('Şifreler eşleşmiyor.');
                  return;
                }
                if (password.length < 6) {
                  setError('Şifre en az 6 karakter olmalıdır.');
                  return;
                }
                if (!termsAccepted) {
                  setTermsError(true);
                  setError('Devam etmek için kullanım şartlarını kabul etmelisiniz.');
                  return;
                }
                setTermsError(false);
                setStep(2);
              }}
              sx={{
                mt: 3,
                mb: 1,
                py: 1.5,
                borderRadius: 2,
                background: colors.gradients.primary,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryMedium,
                  boxShadow: colors.shadows.cardHover,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Devam
            </Button>
            </>
            )}

            {step === 2 && (
              <>
            <Alert
              severity="info"
              icon={<InfoIcon fontSize="small" />}
              sx={{
                mb: 2,
                borderRadius: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { py: 0.25 },
              }}
            >
              <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                Bu bilgiler diyetisyeniniz tarafından kişisel programınızı hazırlamak için kullanılır. Tüm alanlar opsiyoneldir; isterseniz sonra doldurabilirsiniz.
              </Typography>
            </Alert>
            <TextField
              margin="normal"
              fullWidth
              type="number"
              inputProps={{ min: 30, max: 300, step: 0.1 }}
              label="Kilo (kg)"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Örn: 75"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: colors.primary.main },
                  '&.Mui-focused fieldset': { borderColor: colors.primary.main, borderWidth: 2 },
                },
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              type="number"
              inputProps={{ min: 100, max: 250, step: 0.5 }}
              label="Boy (cm)"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Örn: 170"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: colors.primary.main },
                  '&.Mui-focused fieldset': { borderColor: colors.primary.main, borderWidth: 2 },
                },
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              type="number"
              inputProps={{ min: 10, max: 120 }}
              label="Yaş"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="Örn: 28"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: colors.primary.main },
                  '&.Mui-focused fieldset': { borderColor: colors.primary.main, borderWidth: 2 },
                },
              }}
            />
            <FormControl fullWidth margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel id="gender-label">Cinsiyet</InputLabel>
              <Select
                labelId="gender-label"
                label="Cinsiyet"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                sx={{
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.main },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.main },
                }}
              >
                <MenuItem value="">Seçiniz (opsiyonel)</MenuItem>
                <MenuItem value="Erkek">Erkek</MenuItem>
                <MenuItem value="Kadın">Kadın</MenuItem>
                <MenuItem value="Diğer">Diğer</MenuItem>
              </Select>
            </FormControl>
            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={() => { setStep(1); setError(''); }}
                disabled={loading}
                sx={{ py: 1.5, borderRadius: 2, borderColor: colors.primary.main, color: colors.primary.main , '&:hover': { borderColor: colors.primary.medium, bgcolor: colors.alpha.primary10, color: colors.primary.dark } }}
              >
                Geri
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || googleLoading}
                startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: colors.gradients.primary,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: colors.shadows.button,
                  '&:hover': {
                    background: colors.gradients.primaryMedium,
                    boxShadow: colors.shadows.cardHover,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? 'Hesap Oluşturuluyor...' : 'Hesap Oluştur'}
              </Button>
            </Box>
            </>
            )}

            {step === 1 && (
            <>
            {/* Google ile Kayıt Butonu */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={handleGoogleRegister}
                disabled={loading || googleLoading}
                startIcon={<Google />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: '#db4437',
                  color: '#db4437',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#c23321',
                    backgroundColor: 'rgba(219, 68, 55, 0.08)',
                    borderWidth: 2,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {googleLoading
                  ? 'Google ile kayıt yapılıyor...'
                  : 'Google ile Kayıt Ol'}
              </Button>
            </Box>

            {/* Ayırıcı */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                my: 2,
              }}
            >
              <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.12)' }} />
              <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                veya
              </Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.12)' }} />
            </Box>
            </>
            )}

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Zaten hesabınız var mı?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/client/login')}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  Giriş Yapın
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </div>
      </div>

      {/* Google Sign-In Complete Modal */}
      {googleUserData && (
        <GoogleSignInCompleteModal
          open={showGoogleCompleteModal}
          userRole="client"
          email={googleUserData.email}
          firstName={googleUserData.firstName}
          lastName={googleUserData.lastName}
          onComplete={handleGoogleCompleteModalSubmit}
          onCancel={handleGoogleCompleteModalCancel}
          loading={googleLoading}
        />
      )}
    </div>
  );
};

export default ClientRegister;
